import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Bug, X } from 'lucide-react';

/**
 * Floating bug control + right-edge drawer for admin debug panels.
 * Pass one or more <DebugPanel ... /> as children.
 */
export function DebugDrawer({ enabled, title = 'Admin debug', children }) {
  const [open, setOpen] = useState(false);
  const closeBtnRef = useRef(null);
  const drawerId = useId();

  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  if (!enabled) return null;

  const panels = React.Children.toArray(children).filter(Boolean);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-4 z-[200] flex h-12 w-12 items-center justify-center rounded-full border border-amber-400/40 bg-amber-500/95 text-zinc-900 shadow-lg shadow-black/35 transition hover:bg-amber-400 hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-200 active:scale-[0.97]"
        aria-label="Open admin debug drawer"
        aria-expanded={open}
        aria-controls={drawerId}
      >
        <Bug className="h-6 w-6" strokeWidth={2.25} aria-hidden />
      </button>

      <div
        className={`fixed inset-0 z-[210] bg-black/55 transition-opacity duration-300 ease-out ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden={!open}
        onClick={close}
      />

      <div
        id={drawerId}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed inset-y-0 right-0 z-[220] flex w-full max-w-lg flex-col border-l border-amber-400/20 bg-beachvision-card shadow-[-12px_0_40px_rgba(0,0,0,0.45)] transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'pointer-events-none translate-x-full'
        }`}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-400/15 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate font-display text-xs font-black uppercase tracking-[0.18em] text-amber-300">
              {title}
            </p>
            <p className="mt-0.5 text-[11px] text-beachvision-muted">Staff-only pipeline details</p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={close}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-300/80"
            aria-label="Close debug drawer"
          >
            <X className="h-5 w-5" strokeWidth={2} aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          {panels.length ? (
            <div className="flex flex-col gap-3">{panels}</div>
          ) : (
            <p className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-6 text-center text-sm text-beachvision-muted">
              No debug rows for this screen.
            </p>
          )}
        </div>
      </div>
    </>
  );
}
